const REQUEST_USERNAME_MUTATION = `
  mutation RequestUsername($requestUsername: RequestUsernameInput!) {
    requestUsername(requestUsername: $requestUsername) {
      result
    }
  }
`;

export type RequestUsernameErrorCode =
  | 'FORGOT_USERNAME_USER_NOT_FOUND'
  | 'CAPTCHA_VERIFICATION_FAILED'
  | 'UNKNOWN_ERROR';

type GraphQLErrorResponse = {
  message?: string;
  extensions?: {
    code?: string;
  };
};

type RequestUsernameGraphQLResponse = {
  data?: {
    requestUsername?: {
      result?: string | null;
    } | null;
  } | null;
  errors?: GraphQLErrorResponse[];
};

export class RequestUsernameError extends Error {
  code: RequestUsernameErrorCode;

  constructor(code: RequestUsernameErrorCode, message?: string) {
    super(message || code);
    this.name = 'RequestUsernameError';
    this.code = code;
  }
}

const KNOWN_ERROR_CODES: readonly RequestUsernameErrorCode[] = [
  'FORGOT_USERNAME_USER_NOT_FOUND',
  'CAPTCHA_VERIFICATION_FAILED',
  'UNKNOWN_ERROR',
];

const isKnownErrorCode = (code: unknown): code is RequestUsernameErrorCode =>
  typeof code === 'string' &&
  KNOWN_ERROR_CODES.includes(code as RequestUsernameErrorCode);

const getFirstGraphQLError = (
  body?: RequestUsernameGraphQLResponse
): RequestUsernameError | undefined => {
  const error = body?.errors?.[0];

  if (!error) {
    return undefined;
  }

  const code = isKnownErrorCode(error.extensions?.code)
    ? error.extensions.code
    : 'UNKNOWN_ERROR';

  return new RequestUsernameError(code, error.message);
};

const readJson = async (
  response: Response
): Promise<RequestUsernameGraphQLResponse | undefined> => {
  try {
    return (await response.json()) as RequestUsernameGraphQLResponse;
  } catch {
    return undefined;
  }
};

interface RequestUsernameOptions {
  baseUrl: string;
  email: string;
  captchaToken: string;
}

export const requestUsername = async ({
  baseUrl,
  email,
  captchaToken,
}: RequestUsernameOptions): Promise<void> => {
  const response = await fetch(new URL('/api/graphql', baseUrl).toString(), {
    method: 'POST',
    credentials: 'omit',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query: REQUEST_USERNAME_MUTATION,
      variables: {
        requestUsername: {
          email,
          captchaToken,
          captchaProvider: 'turnstile',
        },
      },
    }),
  });
  const body = await readJson(response);
  const graphQLError = getFirstGraphQLError(body);

  if (graphQLError) {
    throw graphQLError;
  }

  if (!response.ok || body?.data?.requestUsername?.result !== 'SUCCESS') {
    throw new RequestUsernameError('UNKNOWN_ERROR');
  }
};
